import { DpeState, PropertyCreationStep, PropertyDto } from '@workspace/data-types';

export interface MissingPropertyInfo {
  id: string;
  step: PropertyCreationStep;
  anchorId: string;
  section: string;
  title: string;
  description: string;
  icon: string;
  severity: 'critical' | 'warning' | 'info';
  actionLabel: string;
}

export class PropertyMissingInfoUtils {
  static readonly TOTAL_EVALUATED_CHECKS = 10;

  /**
   * Evaluates and returns all missing items for a given property.
   */
  static getAllMissingItems(prop: PropertyDto | null | undefined): MissingPropertyInfo[] {
    if (!prop) return [];
    const items: MissingPropertyInfo[] = [];

    // 1. Photos (au moins 3 photos)
    const photoCount = prop.media?.photos?.length ?? 0;
    if (photoCount < 3) {
      items.push({
        id: 'missing-photos',
        step: PropertyCreationStep.PHOTOS,
        anchorId: 'photos-section',
        section: 'Photos',
        title: photoCount === 0 ? 'Au moins 3 photos requises' : `Photos insuffisantes (${photoCount}/3)`,
        description:
          photoCount === 0
            ? 'Ajoutez au moins 3 photos du bien pour maximiser la visibilité et l’attractivité de votre annonce.'
            : `Ajoutez encore ${3 - photoCount} photo${3 - photoCount > 1 ? 's' : ''} pour atteindre le minimum requis de 3 photos.`,
        icon: 'add_a_photo',
        severity: 'warning',
        actionLabel: 'Ajouter des photos',
      });
    }

    // 2. Présentation (Titre & Description)
    const title = prop.presentation?.title?.trim();
    const desc = prop.presentation?.description?.trim();
    if (!title || !desc) {
      items.push({
        id: 'missing-presentation',
        step: PropertyCreationStep.PRESENTATION,
        anchorId: 'presentation-section',
        section: 'Présentation',
        title: 'Titre ou description manquant',
        description: 'Une présentation soignée et détaillée valorise les points forts de votre bien.',
        icon: 'description',
        severity: 'warning',
        actionLabel: "Rédiger l'annonce",
      });
    }

    // 3. Espaces Intérieurs / Chambres
    const interior = prop.characteristics?.interior;
    const hasRooms = (interior?.rooms?.length ?? 0) > 0;
    const hasBedrooms = (interior?.bedroom?.length ?? 0) > 0;
    if (!hasRooms && !hasBedrooms) {
      items.push({
        id: 'missing-interior-spaces',
        step: PropertyCreationStep.CHARACTERISTICS,
        anchorId: 'characteristics-section',
        section: 'Caractéristiques',
        title: 'Espaces intérieurs non renseignés',
        description: 'Indiquez au moins une pièce principale (salon, séjour, cuisine...) ou une chambre.',
        icon: 'meeting_room',
        severity: 'warning',
        actionLabel: 'Ajouter des pièces',
      });
    }

    // 4. Concordance Surface des pièces intérieures / Surface au sol
    const targetGround =
      typeof prop.keyInfo?.surface?.ground === 'number' && prop.keyInfo.surface.ground > 0
        ? prop.keyInfo.surface.ground
        : typeof prop.keyInfo?.surface?.habitable === 'number' && prop.keyInfo.surface.habitable > 0
          ? prop.keyInfo.surface.habitable
          : null;

    if (targetGround != null && targetGround > 0) {
      let totalInteriorSurface = 0;
      const addSpaces = (list?: Array<{ surface?: number | null }>) => {
        if (Array.isArray(list)) {
          for (const item of list) {
            const s = Number(item?.surface);
            if (!Number.isNaN(s) && s > 0) {
              totalInteriorSurface += s;
            }
          }
        }
      };
      if (interior) {
        addSpaces(interior.rooms);
        addSpaces(interior.bedroom);
        addSpaces(interior.bathroom);
        addSpaces(interior.showerRoom);
      }
      totalInteriorSurface = Math.round(totalInteriorSurface * 100) / 100;

      const tolerance = Math.max(0.1, targetGround * 0.01);
      const diff = totalInteriorSurface - targetGround;

      if (Math.abs(diff) > tolerance) {
        let reminderTitle: string;
        let reminderDesc: string;

        if (totalInteriorSurface === 0) {
          reminderTitle = 'Superficie des pièces non renseignée';
          reminderDesc = `La superficie totale des pièces (0 m²) ne correspond pas à la surface au sol attendue (${targetGround} m²).`;
        } else if (diff < 0) {
          reminderTitle = 'Superficie des pièces incomplète';
          reminderDesc = `La superficie cumulée des pièces (${totalInteriorSurface} m²) est inférieure à la surface au sol (${targetGround} m²).`;
        } else {
          reminderTitle = 'Superficie des pièces excédentaire';
          reminderDesc = `La superficie cumulée des pièces (${totalInteriorSurface} m²) dépasse la surface au sol (${targetGround} m²).`;
        }

        items.push({
          id: 'missing-interior-surface-match',
          step: PropertyCreationStep.CHARACTERISTICS,
          anchorId: 'characteristics-section',
          section: 'Caractéristiques',
          title: reminderTitle,
          description: reminderDesc,
          icon: 'square_foot',
          severity: 'warning',
          actionLabel: 'Ajuster les pièces',
        });
      }
    }

    // 5. Sanitaires
    const hasWc = (interior?.wc ?? 0) > 0;
    const hasBathrooms = (interior?.bathroom?.length ?? 0) > 0;
    const hasShowers = (interior?.showerRoom?.length ?? 0) > 0;
    if (!hasWc && !hasBathrooms && !hasShowers) {
      items.push({
        id: 'missing-sanitary',
        step: PropertyCreationStep.CHARACTERISTICS,
        anchorId: 'characteristics-section',
        section: 'Caractéristiques',
        title: 'Sanitaires non renseignés',
        description: "Ajoutez au moins un WC, une salle de bain ou une salle d'eau.",
        icon: 'bathtub',
        severity: 'warning',
        actionLabel: 'Ajouter des sanitaires',
      });
    }

    // 5. Bâtiment (Construction)
    const constr = prop.construction;
    const hasYear = !!constr?.year;
    if (!hasYear) {
      items.push({
        id: 'missing-construction',
        step: PropertyCreationStep.CONSTRUCTION,
        anchorId: 'construction-section',
        section: 'Bâtiment',
        title: 'Année de construction non précisée',
        description: "Renseignez l'époque de construction du bien.",
        icon: 'home_work',
        severity: 'info',
        actionLabel: 'Préciser le bâtiment',
      });
    }

    // 6. Finances : Prix de vente
    const financials = prop.financials;
    const hasSellingPrice = financials?.sellingPriceCents != null && financials.sellingPriceCents > 0;
    if (!hasSellingPrice) {
      items.push({
        id: 'missing-price',
        step: PropertyCreationStep.FINANCIALS,
        anchorId: 'financials-section',
        section: 'Finances',
        title: 'Prix de vente manquant',
        description: 'Fixez le montant du prix net vendeur pour votre bien.',
        icon: 'payments',
        severity: 'critical',
        actionLabel: 'Définir le prix',
      });
    }

    // 7. Finances : Charges courantes
    const hasCharges =
      (financials?.chargesCents != null && financials.chargesCents > 0) ||
      (financials?.coOwnershipChargesCents != null && financials.coOwnershipChargesCents > 0);
    if (!hasCharges) {
      items.push({
        id: 'missing-charges',
        step: PropertyCreationStep.FINANCIALS,
        anchorId: 'financials-section',
        section: 'Finances',
        title: 'Charges courantes non renseignées',
        description: 'Indiquez le montant estimé des charges mensuelles ou de copropriété.',
        icon: 'receipt_long',
        severity: 'info',
        actionLabel: 'Indiquer les charges',
      });
    }

    // 8. Finances : Taxe foncière
    const propertyTax = financials?.taxesAndFees?.propertyTaxCents;
    const hasPropertyTax = propertyTax != null && propertyTax > 0;
    if (!hasPropertyTax) {
      items.push({
        id: 'missing-property-tax',
        step: PropertyCreationStep.FINANCIALS,
        anchorId: 'financials-section',
        section: 'Fiscalité',
        title: 'Taxe foncière non renseignée',
        description: 'Le montant annuel de la taxe foncière est un critère essentiel pour les acheteurs.',
        icon: 'gavel',
        severity: 'info',
        actionLabel: 'Renseigner la taxe',
      });
    }

    // 9. Diagnostic DPE
    const dpe = prop.dpe;
    if (!dpe || !dpe.state) {
      items.push({
        id: 'missing-dpe',
        step: PropertyCreationStep.DPE,
        anchorId: 'dpe-section',
        section: 'Diagnostic DPE',
        title: 'DPE non renseigné',
        description: 'Le Diagnostic de Performance Énergétique est indispensable pour publier votre bien.',
        icon: 'bolt',
        severity: 'critical',
        actionLabel: 'Renseigner le DPE',
      });
    } else if (dpe.state === DpeState.TO_DO) {
      items.push({
        id: 'dpe-to-do',
        step: PropertyCreationStep.DPE,
        anchorId: 'dpe-section',
        section: 'Diagnostic DPE',
        title: 'DPE à réaliser',
        description: 'Votre diagnostic est marqué comme « À réaliser ». Planifiez votre diagnostic DPE.',
        icon: 'bolt',
        severity: 'warning',
        actionLabel: 'Mettre à jour le DPE',
      });
    } else if (dpe.state === DpeState.IN_PROGRESS) {
      items.push({
        id: 'dpe-in-progress',
        step: PropertyCreationStep.DPE,
        anchorId: 'dpe-section',
        section: 'Diagnostic DPE',
        title: 'DPE en cours de réalisation',
        description: 'Renseignez les étiquettes énergie et climat dès réception de votre bilan.',
        icon: 'bolt',
        severity: 'info',
        actionLabel: 'Compléter le DPE',
      });
    } else if (dpe.state === DpeState.DONE && (!dpe.energyClass || !dpe.climateClass)) {
      items.push({
        id: 'dpe-missing-classes',
        step: PropertyCreationStep.DPE,
        anchorId: 'dpe-section',
        section: 'Diagnostic DPE',
        title: 'Classes DPE manquantes',
        description:
          "Le DPE est réalisé mais les étiquettes énergie (kWh/m².an) ou climat n'ont pas été saisies.",
        icon: 'bolt',
        severity: 'warning',
        actionLabel: 'Ajouter les classes',
      });
    }

    return items;
  }

  /**
   * Returns missing items specific to a given step.
   */
  static getMissingItemsByStep(
    prop: PropertyDto | null | undefined,
    step: PropertyCreationStep,
  ): MissingPropertyInfo[] {
    return this.getAllMissingItems(prop).filter((item) => item.step === step);
  }
}
